'use client';

import { Fragment } from 'react';
import { motion } from 'framer-motion';

/**
 * Words rise from beneath a mask — like editorial print.
 * Each word is in its own overflow:hidden box; the y-translate happens inside.
 * This produces the crisp "wipe up" reveal of premium SaaS heroes.
 */
export function KineticLine({
  words,
  delay = 0,
  gradient = false,
  light = false,
  justify = 'center',
  className = '',
}: {
  words: string[];
  delay?: number;
  gradient?: boolean;
  light?: boolean;
  justify?: 'center' | 'start';
  className?: string;
}) {
  return (
    <span className={`inline-flex flex-wrap gap-x-[0.22em] ${justify === 'start' ? 'justify-start' : 'justify-center'} ${className}`}>
      {/* The space after each word is for the raw HTML ("Numbers you can", not
          "Numbersyoucan"). Whitespace between flex items is never rendered, so
          the gap above still sets the spacing. */}
      {words.map((w, i) => (
        <Fragment key={`${w}-${i}`}>
          <span className="inline-block overflow-hidden pb-[0.22em] pr-[0.06em] align-bottom">
            <motion.span
              initial={{ y: '100%', opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{
                delay: delay + i * 0.09,
                duration: 0.95,
                ease: [0.16, 1, 0.3, 1],
              }}
              className={`inline-block ${
                gradient
                  ? light
                    ? 'bg-gradient-to-br from-[#7C3AED] via-[#6D28D9] to-[#4C1D95] bg-clip-text text-transparent'
                    : 'bg-gradient-to-br from-[#E9D5FF] via-[#C4B5FD] to-[#A78BFA] bg-clip-text text-transparent'
                  : light
                    ? 'text-gray-900'
                    : 'text-white'
              }`}
              style={{ willChange: 'transform' }}
            >
              {w}
            </motion.span>
          </span>
          {' '}
        </Fragment>
      ))}
    </span>
  );
}

/** A small caps eyebrow with two thin rules around it. */
export function Eyebrow({ children, delay = 0, light = false }: { children: React.ReactNode; delay?: number; light?: boolean }) {
  const color = light ? 'text-purple-700/80' : 'text-purple-200/70';
  const rule = light ? 'to-purple-400/50' : 'to-purple-300/40';
  return (
    <motion.div
      initial={{ opacity: 0, y: -6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.7 }}
      className={`flex items-center gap-4 text-[11px] tracking-[0.32em] uppercase font-medium ${color}`}
    >
      <span className={`h-px w-10 bg-gradient-to-r from-transparent ${rule}`} />
      <span>{children}</span>
      <span className={`h-px w-10 bg-gradient-to-l from-transparent ${rule}`} />
    </motion.div>
  );
}
